import { useCallback, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useAddTradeToMap } from 'hooks/business/conversation';
import {
  tradeList,
  TradeListParams,
  logisticsList,
  lazadaLogisticsList,
  getTradeEligibilityList,
} from 'services/api/conversation';
import { CancelToken } from 'axios';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useSafeState } from 'ahooks';

export const defaultPage = {
  pageNum: 0,
  pageSize: 10,
  total: 0,
};

function useTrade(storeId: string | number) {
  const [page, setPage] = useState(defaultPage);
  const lastFetchParamsRef = useRef<{
    params: TradeListParams;
    platform: General.CaselessPlatformTunnel;
  } | null>(null);

  const [hasMore, setHasMore] = useSafeState(true);

  const [list, setList] = useSafeState<Conversation.Trade.TradeWithLogistics[]>([]);

  const [loading, setLoading] = useSafeState(false);

  const fetchFlag = useRef(0);

  const addTradeToMap = useAddTradeToMap();

  const addLogisticsToTradeList = useCallback(
    (tradeList: Conversation.Trade.Vo[], platform?: General.PlatformUpperCase) => {
      const queryParams = isTiKTokUpperCase(platform)
        ? {
            internalStoreId: storeId,
            externalTradeIds: tradeList.map((_) => _.externalTradeId).join(','),
            needPackageData: true,
          }
        : {
            internal_trade_ids: tradeList.map((_) => _.internalTradeId).join(','),
            needReverse: platform === 'SHOPEE',
          };

      return logisticsList(queryParams)
        .then((res) => {
          if (isTiKTokUpperCase(platform)) {
            return getTradeEligibilityList({
              internalTradeIds: tradeList.map((_) => _.internalTradeId),
            })
              .then(({ orderEligibilityList }) => {
                return {
                  ...res.data,
                  orderEligibilityList,
                };
              })
              .catch(() => {
                return {
                  ...res.data,
                  orderEligibilityList: [],
                };
              });
          }
          return {
            ...res.data,
            orderEligibilityList: [],
          };
        })
        .then((res) => {
          const {
            data: logisticsList,
            thirdTrackingInfo = [],
            reverseData = [],
            orderEligibilityList,
          } = res;

          const tradeIdLogisticsListMap = logisticsList.reduce<
            Record<string, Conversation.Trade.Logistics[]>
          >((res, logistics) => {
            const { internalTradeId } = logistics;

            if (!res[internalTradeId]) {
              res[internalTradeId] = [];
            }

            res[internalTradeId].push(logistics);

            return res;
          }, {});

          const tradeIdThirdLogisticsListMap = thirdTrackingInfo.reduce<
            Record<string, Conversation.Trade.ThirdLogistics[]>
          >((res, logistics) => {
            const { internalTradeId } = logistics;

            if (!res[internalTradeId]) {
              res[internalTradeId] = [];
            }

            res[internalTradeId].push(logistics);

            return res;
          }, {});

          const tradeIdReverseLogisticsListMap = reverseData.reduce<
            Record<string, Conversation.Trade.ReverseLogistics[]>
          >((res, logistics) => {
            const { internalTradeId } = logistics;

            if (!res[internalTradeId]) {
              res[internalTradeId] = [];
            }

            res[internalTradeId].push(logistics);

            return res;
          }, {});

          setList((list) => {
            return list.map((trade) => {
              const { internalTradeId, externalTradeId } = trade;

              const { [internalTradeId]: logisticsList } = tradeIdLogisticsListMap;

              const { [internalTradeId]: thirdLogisticsList } = tradeIdThirdLogisticsListMap;

              const { [internalTradeId]: reverseLogisticsList } = tradeIdReverseLogisticsListMap;

              const eligibility = orderEligibilityList.find((_) => _.orderId === externalTradeId);

              if (
                logisticsList === undefined &&
                thirdLogisticsList === undefined &&
                reverseLogisticsList === undefined &&
                eligibility === undefined
              ) {
                return trade;
              }

              return {
                ...trade,
                logisticsList,
                thirdLogisticsList,
                reverseLogisticsList,
                eligibility,
              };
            });
          });
        });
    },
    [setList, storeId],
  );

  const addMercadoLogisticsToTradeList = useCallback(
    (tradeList: Conversation.Trade.Vo[]) => {
      const queryParams = {
        internal_trade_ids: tradeList.map((_) => _.internalTradeId).join(','),
      };

      return logisticsList(queryParams).then((res) => {
        const {
          data: { data: logisticsList },
        } = res;

        const tradeIdLogisticsMap = logisticsList.reduce<Record<string, any>>((res, logistics) => {
          const { internalTradeId } = logistics;

          res[internalTradeId] = logistics;

          return res;
        }, {});

        setList((list) => {
          return list.map((trade) => {
            const { internalTradeId } = trade;

            const { [internalTradeId]: logistics } = tradeIdLogisticsMap;

            if (logistics === undefined) {
              return trade;
            }

            return {
              ...trade,
              mercadoLogistics: logistics,
            };
          });
        });
      });
    },
    [setList],
  );

  const addLazadaLogisticsToTradeList = useCallback(
    (tradeList: Conversation.Trade.Vo[]) => {
      const queryParams = {
        internal_trade_ids: tradeList.map((_) => _.internalTradeId).join(','),
      };

      return lazadaLogisticsList(queryParams).then((res) => {
        const {
          data: { data: logisticsList, thirdTrackingInfo = [] },
        } = res;

        const tradeIdLogisticsMap = logisticsList.reduce<
          Record<string, Conversation.Trade.LazadaLogistics>
        >((res, logistics) => {
          const { internalTradeId } = logistics;

          res[internalTradeId] = logistics;

          return res;
        }, {});

        const tradeIdThirdLogisticsListMap = thirdTrackingInfo.reduce<
          Record<string, Conversation.Trade.ThirdLogistics[]>
        >((res, logistics) => {
          const { internalTradeId } = logistics;

          if (!res[internalTradeId]) {
            res[internalTradeId] = [];
          }

          res[internalTradeId].push(logistics);

          return res;
        }, {});

        setList((list) => {
          return list.map((trade) => {
            const { internalTradeId } = trade;

            const { [internalTradeId]: logistics } = tradeIdLogisticsMap;

            const { [internalTradeId]: thirdLogisticsList } = tradeIdThirdLogisticsListMap;

            if (logistics === undefined && thirdLogisticsList === undefined) {
              return trade;
            }

            return {
              ...trade,
              lazadaLogistics: logistics,
              thirdLogisticsList,
            };
          });
        });
      });
    },
    [setList],
  );

  const fetchList = useCallback(
    (
      params: TradeListParams,
      platform: General.CaselessPlatformTunnel,
      newCancelToken?: () => CancelToken,
    ) => {
      lastFetchParamsRef.current = { params, platform };
      fetchFlag.current += 1;
      const { current: currentFetchFlag } = fetchFlag;

      setLoading(true);

      return tradeList(params, newCancelToken)
        .then((res) => {
          const { pageInfo, tradeList = [] } = res;

          addTradeToMap(tradeList);

          const phoneList: string[] = [];
          tradeList.forEach((t) => {
            if (t.phone && t.phone !== '') {
              phoneList.push(t.phone);
            }
          });

          if (currentFetchFlag !== fetchFlag.current) {
            return;
          }

          const { pageNo: pageNum, pageSize, totalCount: total } = pageInfo;

          batchedUpdates(() => {
            setPage({
              pageNum,
              pageSize,
              total,
            });

            setHasMore(pageNum * pageSize < total);

            setList((oldList) => {
              const merged = tradeList.reduce(
                (res, it) => {
                  const existed = oldList.findIndex(
                    (itt) => itt.internalTradeId === it.internalTradeId,
                  );
                  if (existed < 0) {
                    res.push(it);
                  } else {
                    res[existed] = {
                      ...oldList[existed],
                      ...it,
                    };
                  }
                  return res;
                },
                [...oldList],
              );

              return merged;
            });

            setLoading(false);
          });

          if (tradeList.length === 0) {
            return;
          }

          // 添加物流信息到缓存中
          if (
            platform === 'SHOPEE' ||
            isTiKTokUpperCase(platform) ||
            platform === 'ALIEXPRESS' ||
            platform === 'KWAI'
          ) {
            addLogisticsToTradeList(tradeList, platform);
          }

          if (platform === 'MERCADO') {
            addMercadoLogisticsToTradeList(tradeList);
          }

          if (platform === 'LAZADA') {
            addLazadaLogisticsToTradeList(tradeList);
          }
          if (platform === 'WA_BIZ') {
            const shopeeTradeList = tradeList
              .filter((x) => x.platform.toLowerCase() === 'shopee')
              .slice(0, 10);
            const lazadaTradeList = tradeList
              .filter((x) => x.platform.toLowerCase() === 'lazada')
              .slice(0, 10);
            shopeeTradeList.length && addLogisticsToTradeList(shopeeTradeList);
            lazadaTradeList.length && addLazadaLogisticsToTradeList(lazadaTradeList);
          }
        })
        .catch((err) => {
          if (currentFetchFlag === fetchFlag.current) {
            setLoading(false);
          }

          return Promise.reject(err);
        });
    },
    [
      setLoading,
      addTradeToMap,
      setHasMore,
      setList,
      addLogisticsToTradeList,
      addMercadoLogisticsToTradeList,
      addLazadaLogisticsToTradeList,
    ],
  );

  const refreshTrade = useCallback(
    (params: TradeListParams, platform: General.CaselessPlatformTunnel, addable = false) => {
      return tradeList(params).then((res) => {
        const { tradeList: [trade] = [] } = res;

        if (!trade) {
          return Promise.reject(new Error('Trade not found.'));
        }

        addTradeToMap(trade);

        const { internalTradeId } = trade;

        const params = isTiKTokUpperCase(platform)
          ? {
              internalStoreId: storeId,
              internal_trade_ids: internalTradeId,
              needPackageData: true,
            }
          : {
              internal_trade_ids: internalTradeId,
              needReverse: platform === 'SHOPEE',
            };

        const getTradeWithLogistics: Promise<Conversation.Trade.TradeWithLogistics> =
          platform === 'LAZADA'
            ? lazadaLogisticsList(params).then((res) => {
                const {
                  data: { data: logisticsList, thirdTrackingInfo: thirdLogisticsList },
                } = res;

                return {
                  ...trade,
                  lazadaLogistics: logisticsList[0],
                  thirdLogisticsList,
                };
              })
            : logisticsList(params)
                .then((res) => {
                  if (isTiKTokUpperCase(platform)) {
                    return getTradeEligibilityList({ internalTradeIds: [internalTradeId] })
                      .then(({ orderEligibilityList }) => {
                        return {
                          ...res.data,
                          orderEligibilityList,
                        };
                      })
                      .catch(() => {
                        return {
                          ...res.data,
                          orderEligibilityList: [],
                        };
                      });
                  }
                  return {
                    ...res.data,
                    orderEligibilityList: [],
                  };
                })
                .then((res) => {
                  const {
                    data: logisticsList,
                    thirdTrackingInfo: thirdLogisticsList,
                    reverseData,
                    orderEligibilityList,
                  } = res;

                  return {
                    ...trade,
                    logisticsList,
                    thirdLogisticsList,
                    reverseLogisticsList: reverseData,
                    eligibility: orderEligibilityList?.[0],
                  };
                });

        return getTradeWithLogistics.then((tradeWithLogistics) => {
          setList((list) => {
            const index = list.findIndex(
              (_) => _.internalTradeId === tradeWithLogistics.internalTradeId,
            );

            if (index >= 0) {
              return [...list.slice(0, index), tradeWithLogistics, ...list.slice(index + 1)];
            }

            if (addable) {
              return [...list, tradeWithLogistics];
            }

            return list;
          });

          return tradeWithLogistics;
        });
      });
    },
    [addTradeToMap, setList, storeId],
  );

  return {
    page,
    hasMore,
    list,
    loading,
    lastFetchParamsRef,
    setPage,
    setHasMore,
    setList,
    setLoading,
    fetchList,
    refreshTrade,
  };
}

export default useTrade;
